import { Card, Descriptions, List, Tabs, Tag, Typography, Image } from "antd";
import {
  Calendar,
  DollarSign,
  FileText,
  MapPin,
  Package,
  Truck,
  Users,
  X,
  Wrench,
  Ruler,
  Scale,
  ChevronDown,
  ExternalLink,
  ChevronRight,
  Plus,
} from "lucide-react";
import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TBooking, TServiceTicket, TInventory } from "~/types";
import { capitalize, formatDate, getClientBookingRelationship, getServiceDisplayTime } from "~/utils";
import { JobStatus, ServiceType, SortOrder, ContainerType, PackingTypes } from "~/utils/constants";
import { useAuth } from "~/store";
import { useGetFiles } from "~/services/file";
import { ServiceDetailSidebar } from "./service-detail-sidebar";
import { ServiceCompletionSidebar } from "./service-completion-sidebar";
import { AddServiceSidebar } from "./add-service-sidebar";
import { calculateServiceCost } from "~/utils/service-cost-calculator";
import { useGetStaffRoleByName } from "~/services/common";
import { useQuery } from "@tanstack/react-query";
import { addOnServicesApi } from "~/services/add-on-services/add-on-services.api";
import { getInvoicesForBooking } from "~/services/billing/billing.api";
import dayjs from "dayjs";

const { Text, Title } = Typography;

interface BookingDetailSidebarProps {
  booking: TBooking | null;
  onClose: () => void;
}

type BookingDetailTab = "services" | "inventory" | "documents" | "billing";

const getStatusBadge = (status: JobStatus) => {
  // In Progress is special - just italic black text, no box
  if (status === JobStatus.IN_PROGRESS) {
    return (
      <span className="text-[#1a1a2e] italic font-bold text-xs uppercase">
        In Progress
      </span>
    );
  }
  
  // Scheduled is special - just black text, no box, no italics
  if (status === JobStatus.SCHEDULED) {
    return (
      <span className="text-[#1a1a2e] font-bold text-xs uppercase">
        Scheduled
      </span>
    );
  }
  
  const statusConfig = {
    [JobStatus.DRAFT]: { text: "Draft", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    [JobStatus.COMPLETED]: { text: "Completed", className: "bg-[#1a1a2e] text-white", extraClass: "" },
    [JobStatus.CANCELLED]: { text: "Cancelled", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    [JobStatus.ESTIMATED]: { text: "Estimated", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
  };
  const config = statusConfig[status] || { text: status, className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" };
  return (
    <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg ${config.className}`}>
      {config.text}
    </span>
  );
};

const getServiceTypeIcon = (type: ServiceType) => {
  switch (type) {
    case ServiceType.DELIVERY:
      return <Truck size={14} />;
    case ServiceType.COLLECTION:
      return <Package size={14} />;
    default:
      return <Calendar size={14} />;
  }
};

export const BookingDetailSidebar: React.FC<BookingDetailSidebarProps> = ({
  booking,
  onClose,
}) => {
  const { user } = useAuth();
  const currentClientId = user?.client?.id;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BookingDetailTab>("services");
  const [selectedService, setSelectedService] = useState<TServiceTicket | null>(null);
  const [isAddServiceSidebarOpen, setIsAddServiceSidebarOpen] = useState(false);

  const { data: documentsData, isLoading: documentsLoading } = useGetFiles({
    jobTicketId: booking?.id,
    sortByCreatedAt: SortOrder.DESC,
  });

  // Determine client relationship and filter data accordingly
  const relationshipData = useMemo(() => {
    if (!booking || !currentClientId) return null;
    return getClientBookingRelationship(booking, currentClientId);
  }, [booking, currentClientId]);

  // Use filtered service tickets based on client relationship
  const visibleServiceTickets = useMemo(() => {
    if (!booking) return [];
    if (!relationshipData) return booking.serviceTicket || [];

    return relationshipData.relationship === "service-client"
      ? relationshipData.visibleServiceTickets
      : booking.serviceTicket || [];
  }, [booking, relationshipData]);

  // Get ESTIMATED and SCHEDULED services from the booking (for pricing display)
  const estimatedServices = useMemo(() => {
    if (!booking || !visibleServiceTickets) return [];
    return visibleServiceTickets.filter(
      (service: TServiceTicket) => 
        service.status === JobStatus.ESTIMATED || service.status === JobStatus.SCHEDULED
    );
  }, [booking, visibleServiceTickets]);

  // Get unique inventory items across all service tickets
  const uniqueInventories = useMemo(() => {
    if (!visibleServiceTickets) return [];
    const inventoryMap = new Map<string, TInventory>();
    visibleServiceTickets.forEach((ticket) => {
      ticket.inventories?.forEach((inventory) => {
        if (!inventoryMap.has(inventory.id)) {
          inventoryMap.set(inventory.id, inventory);
        }
      });
    });
    return Array.from(inventoryMap.values());
  }, [visibleServiceTickets]);

  // Calculate totals for billing
  const billingInfo = useMemo(() => {
    if (!booking) return null;
    
    const total = booking.payment?.total || 0;
    const paid = booking.payment?.paid || 0;
    const due = booking.payment?.due || 0;
    const invoiceId = booking.payment?.invoiceId;
    const dueDate = booking.payment?.dueDate;

    return {
      total,
      paid,
      due,
      invoiceId,
      dueDate,
    };
  }, [booking]);

  // Inventory Tab Content - hooks must be defined before any conditional returns
  const handleViewInInventory = React.useCallback((inventory: TInventory) => {
    const searchTerm = inventory.inventoryRef || inventory.title || "";
    const params = new URLSearchParams();
    params.set("item", inventory.id);
    if (searchTerm) {
      params.set("search", searchTerm);
    }
    navigate(`/inventory?${params.toString()}`);
  }, [navigate]);

  const formatPackingType = React.useCallback((packingType?: PackingTypes, containerType?: ContainerType) => {
    if (containerType) {
      const containerMap: Record<ContainerType, string> = {
        [ContainerType.CRATE]: "Crate",
        [ContainerType.PACKAGE]: "Package",
        [ContainerType.PIECE]: "Piece",
        [ContainerType.T_Frame]: "T Frame",
        [ContainerType.SLIPCASE]: "Slipcase",
        [ContainerType.BOX]: "Box",
        [ContainerType.CUSTOM]: "Custom",
        [ContainerType.SLATCRATE]: "Slat Crate",
        [ContainerType.CBIN]: "C-Bin",
        [ContainerType.PALLET]: "Pallet",
      };
      return containerMap[containerType] || containerType;
    }
    if (!packingType) return null;
    return capitalize(packingType.replace(/_/g, " "));
  }, []);

  // Track which service items are expanded - MUST be before any conditional returns
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());

  const toggleServiceExpanded = useCallback((serviceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedServices(prev => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  }, []);

  const handleServiceClick = useCallback((service: TServiceTicket, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    // Open the service detail sidebar
    console.log('Opening service detail sidebar for service:', service.id, service.serial);
    setSelectedService(service);
  }, []);

  // Track which estimate is expanded - MUST be before any conditional returns
  const [expandedEstimate, setExpandedEstimate] = useState<string | null>(null);
  
  // Track which service's completion details to show - MUST be before any conditional returns
  const [completionService, setCompletionService] = useState<TServiceTicket | null>(null);

  const handleCloseServiceSidebar = useCallback(() => {
    setSelectedService(null);
  }, []);

  const handleViewCompletionDetails = useCallback((service: TServiceTicket, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompletionService(service);
  }, []);

  const handleCloseCompletionSidebar = useCallback(() => {
    setCompletionService(null);
  }, []);

  // Helper functions for estimates
  // Get Art Handler role data for staff rates - MUST be before early return
  const { data: artHandlerRole } = useGetStaffRoleByName("Art Handler", booking?.clientId);

  // Get add-on services data for pricing - MUST be before early return
  const { data: addOnServices } = useQuery({
    queryKey: ["add-on-services"],
    queryFn: () => addOnServicesApi.getAll(),
  });

  // Debug logging - MUST be before early return
  React.useEffect(() => {
    if (selectedService) {
      console.log('Service sidebar should be open:', selectedService.id, selectedService.serial);
    }
  }, [selectedService]);

  if (!booking) return null;

  const isServiceSidebarOpen = selectedService !== null;
  const isCompletionSidebarOpen = completionService !== null;

  // Services Tab Content
  const servicesContent = (
    <div className="space-y-3">
      {relationshipData?.relationship === "service-client" && (
        <Card size="small" className="border border-[#e5e5e5] rounded-lg bg-white">
          <div className="flex items-center space-x-2">
            <Tag className="border border-[#e5e5e5] rounded-lg bg-[#1a1a2e] text-white">Service Client</Tag>
            <Text className="text-sm text-[#1a1a2e]">
              You have limited access to this booking as a service client. Only relevant service information is shown.
            </Text>
          </div>
        </Card>
      )}

      {visibleServiceTickets.length === 0 ? (
        <Text className="text-[#1a1a2e]">No services found for this booking.</Text>
      ) : (
        <div className="space-y-2">
          {visibleServiceTickets.map((service) => {
            const isExpanded = expandedServices.has(service.id);
            const serviceClient = service.client;
            const serviceClientName = serviceClient?.companyName || 
              (serviceClient?.firstName && serviceClient?.lastName 
                ? `${serviceClient.firstName} ${serviceClient.lastName}` 
                : serviceClient?.email || 'N/A');

            return (
              <div
                key={service.id}
                className="border border-[#e5e5e5] rounded-lg bg-white"
              >
                {/* Collapsed Header - Always Visible */}
                <div
                  className="p-3 cursor-pointer hover:bg-[#fafafa] transition-colors"
                  onClick={(e) => toggleServiceExpanded(service.id, e)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      {getServiceTypeIcon(service.serviceType)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Text strong className="text-sm text-[#1a1a2e]">
                            {service.serial}
                          </Text>
                          <Text className="text-xs text-[#1a1a2e] uppercase">
                            {capitalize(service.serviceType)}
                          </Text>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[#1a1a2e]">
                          {service.serviceDatetime && (
                            <div className="flex items-center gap-1">
                              <Calendar size={12} />
                              <span>{dayjs(service.serviceDatetime).format('MMM D, YYYY')}</span>
                            </div>
                          )}
                          {serviceClientName && (
                            <div className="flex items-center gap-1">
                              <Users size={12} />
                              <span>{serviceClientName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(service.status)}
                      <ChevronRight 
                        size={16} 
                        className={`text-[#1a1a2e] transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div 
                    className="border-t border-[#e5e5e5] p-3 space-y-3 bg-white"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {service.serviceDatetime && (
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-[#1a1a2e]" />
                        <Text className="text-sm text-[#1a1a2e]">
                          {(() => {
                            const displayTime = getServiceDisplayTime(service.serviceDatetime, service.startDatetime);
                            if (!displayTime) return "-";
                            return displayTime.format('MMM D, YYYY h:mm A');
                          })()}
                        </Text>
                      </div>
                    )}

                    {service.location && (
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-[#1a1a2e]" />
                        <Text className="text-sm text-[#1a1a2e]">
                          {service.location.city || ""}
                          {service.location.city && service.location.state ? ", " : ""}
                          {service.location.state || ""}
                        </Text>
                      </div>
                    )}

                    {/* Scope */}
                    {service.scope && (
                      <div>
                        <Text className="text-xs font-bold uppercase text-[#1a1a2e] block mb-1">
                          Scope:
                        </Text>
                        <Text className="text-sm text-[#1a1a2e]">{service.scope}</Text>
                      </div>
                    )}

                    {/* Resources Section */}
                    {(service.vessel || (service.staffs && service.staffs.length > 0) || (service.supplyItems && service.supplyItems.length > 0)) && (
                      <div className="pt-2 border-t border-[#e5e5e5]">
                        <Text className="text-xs font-bold uppercase text-[#1a1a2e] block mb-2">
                          Resources:
                        </Text>
                        <div className="space-y-1.5">
                          {/* Vessel/Truck */}
                          {service.vessel && (
                            <div className="flex items-center gap-2">
                              <Truck size={12} className="text-[#1a1a2e]" />
                              <Text className="text-xs text-[#1a1a2e]">
                                <Text strong>Truck:</Text> {service.vessel.name}
                                {service.vessel.licensePlate && ` (${service.vessel.licensePlate})`}
                              </Text>
                            </div>
                          )}

                          {/* Staff */}
                          {service.staffs && service.staffs.length > 0 && (
                            <div className="flex items-center gap-2">
                              <Users size={12} className="text-[#1a1a2e]" />
                              <Text className="text-xs text-[#1a1a2e]">
                                <Text strong>Staff:</Text> {service.staffs.map(s => {
                                  const user = s.user;
                                  if (user?.firstName && user?.lastName) {
                                    return `${user.firstName} ${user.lastName}`;
                                  }
                                  return user?.email || 'Unknown';
                                }).join(', ')}
                              </Text>
                            </div>
                          )}

                          {/* Materials/Supplies */}
                          {service.supplyItems && service.supplyItems.length > 0 && (
                            <div className="flex items-start gap-2">
                              <Wrench size={12} className="text-[#1a1a2e] mt-0.5" />
                              <div className="flex-1">
                                <Text className="text-xs text-[#1a1a2e]">
                                  <Text strong>Materials:</Text>
                                </Text>
                                <div className="mt-0.5 space-y-0.5">
                                  {service.supplyItems.map((item, idx) => (
                                    <Text key={idx} className="text-xs block text-[#1a1a2e]">
                                      • {item.supply?.name || 'Unknown'} {item.quantity > 1 && `(${item.quantity}${item.supply?.unit ? ` ${item.supply.unit}` : ''})`}
                                    </Text>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {service.inventories && service.inventories.length > 0 && (
                      <div className="pt-2 border-t border-[#e5e5e5]">
                        <Text className="text-xs text-[#1a1a2e]">
                          {service.inventories.length} {service.inventories.length === 1 ? 'item' : 'items'}
                        </Text>
                      </div>
                    )}

                    {/* View Details Button - Only for non-completed services */}
                    {service.status !== JobStatus.COMPLETED && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServiceClick(service, e);
                        }}
                        className="w-full border border-[#e5e5e5] rounded-lg bg-[#1a1a2e] text-white px-4 py-2 font-bold text-xs uppercase tracking-wide hover:bg-[#333] transition-colors mb-2"
                      >
                        View Details
                      </button>
                    )}

                    {/* View Completion Details Button - Only for completed services */}
                    {service.status === JobStatus.COMPLETED && (service.completed_at || service.completedAt) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewCompletionDetails(service, e);
                        }}
                        className="w-full border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-2 font-bold text-xs uppercase tracking-wide hover:bg-[#fafafa] transition-colors"
                      >
                        View Completion Details
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Add Service Button - Only show when services tab is active and booking is not completed */}
      {activeTab === "services" && booking?.status !== JobStatus.COMPLETED && (
        <div className="mt-4 pt-4 border-t border-[#e5e5e5]">
          <button
            onClick={() => setIsAddServiceSidebarOpen(true)}
            className="w-full border border-[#e5e5e5] rounded-lg bg-[#1a1a2e] text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#333] transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            Add Service
          </button>
        </div>
      )}
    </div>
  );

  const InventoryExpandableItem: React.FC<{ item: TInventory }> = React.memo(({ item }) => {
    const [expanded, setExpanded] = useState(false);

    if (!item) return null;

    const hasDimensions = item.length || item.width || item.height;
    const dimensionsDisplay = hasDimensions
      ? `${item.length || 0}" × ${item.width || 0}" × ${item.height || 0}"`
      : null;

    const hasImages = item.images && Array.isArray(item.images) && item.images.length > 0;

    return (
      <div className="border border-neutral-200 rounded-lg overflow-hidden">
        {/* Header - always visible */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-3 flex items-center justify-between hover:bg-neutral-50 transition-colors text-left"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Package size={16} className="text-neutral-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              {/* Title - bold, on top */}
              {item.title && (
                <Text strong className="block text-sm mb-1 text-neutral-900">
                  {item.title}
                </Text>
              )}
              {/* Artist - below title */}
              {item.artistName && (
                <Text className="text-xs block mb-1 text-neutral-600">
                  by {item.artistName}
                </Text>
              )}
              {/* Item # and Client Reference # */}
              <div className="space-y-0.5">
                {item.inventoryRef && (
                  <Text className="text-xs text-neutral-700">
                    Item #: {item.inventoryRef}
                  </Text>
                )}
                {item.clientInternalRef && (
                  <Text className="text-xs text-neutral-700">
                    Client Ref #: {item.clientInternalRef}
                  </Text>
                )}
              </div>
            </div>
          </div>
          {/* Dimensions and Weight on the right */}
          <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-3">
            {dimensionsDisplay && (
              <div className="flex items-center gap-1">
                <Ruler size={12} className="text-neutral-600" />
                <Text className="text-xs whitespace-nowrap text-neutral-700">
                  {dimensionsDisplay}
                </Text>
              </div>
            )}
            {item.weight > 0 && (
              <div className="flex items-center gap-1">
                <Scale size={12} className="text-neutral-600" />
                <Text className="text-xs whitespace-nowrap text-neutral-700">
                  {item.weight} lbs
                </Text>
              </div>
            )}
            <ChevronDown
              size={16}
              className={`text-neutral-400 transition-transform flex-shrink-0 mt-1 ${
                expanded ? "rotate-180" : ""
              }`}
            />
          </div>
        </button>

        {/* Expanded content */}
        {expanded && (
          <div className="px-3 pb-3 border-t border-neutral-100 bg-neutral-50">
            {/* Images */}
            {hasImages && (
              <div className="pt-3 pb-2">
                <div className="flex gap-2 overflow-x-auto">
                  <Image.PreviewGroup>
                    {(item.images || []).slice(0, 4).map((img, idx) => (
                      <Image
                        key={img?.id || idx}
                        src={img?.url || ""}
                        alt={item.title || "Inventory image"}
                        width={80}
                        height={80}
                        className="rounded object-cover"
                        style={{ objectFit: "cover" }}
                      />
                    ))}
                  </Image.PreviewGroup>
                  {item.images && item.images.length > 4 && (
                    <div className="w-20 h-20 bg-neutral-200 rounded flex items-center justify-center text-neutral-500 text-sm flex-shrink-0">
                      +{item.images.length - 4}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Details grid */}
            <div className="pt-2 space-y-2">
              {item.title && (
                <div className="flex justify-between text-sm">
                  <Text type="secondary">Title</Text>
                  <Text className="text-right max-w-[60%] truncate">{item.title}</Text>
                </div>
              )}

              {item.clientInternalRef && (
                <div className="flex justify-between text-sm">
                  <Text type="secondary">Client Ref</Text>
                  <Text className="text-right max-w-[60%] truncate">{item.clientInternalRef}</Text>
                </div>
              )}

              {item.medium && (
                <div className="flex justify-between text-sm">
                  <Text type="secondary">Medium</Text>
                  <Text className="text-right max-w-[60%] truncate">{item.medium}</Text>
                </div>
              )}

              {dimensionsDisplay && (
                <div className="flex justify-between text-sm">
                  <Text type="secondary" className="flex items-center gap-1">
                    <Ruler size={12} /> Dimensions
                  </Text>
                  <Text>{dimensionsDisplay}</Text>
                </div>
              )}

              {item.weight > 0 && (
                <div className="flex justify-between text-sm">
                  <Text type="secondary" className="flex items-center gap-1">
                    <Scale size={12} /> Weight
                  </Text>
                  <Text>{item.weight} lbs</Text>
                </div>
              )}

              {item.value && item.value > 0 && (
                <div className="flex justify-between text-sm">
                  <Text type="secondary">Value</Text>
                  <Text>${item.value.toLocaleString()}</Text>
                </div>
              )}

              {formatPackingType(item.packingType, item.containerType) && (
                <div className="flex justify-between text-sm">
                  <Text type="secondary">Packing</Text>
                  <Tag className="!mr-0">{formatPackingType(item.packingType, item.containerType)}</Tag>
                </div>
              )}

              {item.specialHandling && item.specialHandling.length > 0 && (
                <div className="text-sm">
                  <Text type="secondary" className="block mb-1">Special Handling</Text>
                  <div className="flex flex-wrap gap-1">
                    {item.specialHandling.map((handling, idx) => (
                      <Tag key={idx} color="orange" className="!mr-0">
                        {handling}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {item.description && (
                <div className="text-sm pt-1">
                  <Text type="secondary" className="block mb-1">Description</Text>
                  <Text className="text-xs">{item.description}</Text>
                </div>
              )}

              {item.notes && (
                <div className="text-sm pt-1">
                  <Text type="secondary" className="block mb-1">Notes</Text>
                  <Text className="text-xs">{item.notes}</Text>
                </div>
              )}

              {/* View in Inventory button */}
              <div className="pt-3 border-t border-neutral-200 mt-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewInInventory(item);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                >
                  <ExternalLink size={14} />
                  View in Inventory
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  });

  const inventoryContent = (
    <div className="space-y-4">
      {uniqueInventories.length === 0 ? (
        <Text type="secondary">No inventory items found for this booking.</Text>
      ) : (
        <div className="space-y-2">
          {uniqueInventories.map((inventory) => (
            <InventoryExpandableItem key={inventory.id} item={inventory} />
          ))}
        </div>
      )}
    </div>
  );

  // Documents Tab Content
  const documentsContent = (
    <div className="space-y-4">
      {documentsLoading ? (
        <Text type="secondary">Loading documents...</Text>
      ) : documentsData && documentsData.items.length > 0 ? (
        <List
          dataSource={documentsData.items}
          renderItem={(doc) => (
            <List.Item>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-gray-400" />
                  <div>
                    <Text strong>{doc.title || "Unnamed Document"}</Text>
                  </div>
                </div>
                <Text type="secondary" className="text-xs">
                  {formatDate(doc.createdAt)}
                </Text>
              </div>
            </List.Item>
          )}
        />
      ) : (
        <Text type="secondary">No documents available.</Text>
      )}
    </div>
  );

  // Calculate total for estimated services using the same logic as admin
  const calculateEstimateTotal = (services: TServiceTicket[]): number => {
    if (!services || services.length === 0) return 0;
    
    return services.reduce((sum, service) => {
      const cost = calculateServiceCost({
        service,
        artHandlerRole: artHandlerRole || undefined,
        addOnServices: addOnServices || [],
        serviceOverrides: new Map(), // No overrides in client view
      });
      return sum + cost.total;
    }, 0);
  };

  const formatServiceType = (serviceType: ServiceType): string => {
    return serviceType
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Helper component for billing content that checks invoices
  const BillingContent: React.FC = () => {
    // Get invoices for the booking to check if invoice exists (only when billing tab is active)
    const { data: bookingInvoices } = useQuery({
      queryKey: ["booking-invoices", booking?.id],
      queryFn: () => getInvoicesForBooking(booking!.id),
      enabled: !!booking?.id && activeTab === "billing",
    });

    // Check if invoice exists - either from payment object or from fetched invoices
    const hasInvoice = billingInfo?.invoiceId || (bookingInvoices && bookingInvoices.length > 0);

    return (
    <div className="space-y-4">
      {/* Pricing Section - Show ESTIMATED and SCHEDULED services from booking */}
      {estimatedServices.length > 0 && (
        <div className="border border-[#e5e5e5] rounded-lg bg-white p-4">
          <Text strong className="text-[#1a1a2e] text-base uppercase tracking-wide block mb-4">
            Service Pricing
          </Text>
          <div className="space-y-3">
            {(() => {
              // Group services by booking (in this case, all are from the same booking)
              const estimateTotal = calculateEstimateTotal(estimatedServices);
              const isExpanded = expandedEstimate === booking?.id;

              return (
                <div key={booking?.id} className="border border-[#e5e5e5] rounded-lg bg-white overflow-hidden">
                  {/* Estimate Header */}
                  <div
                    className="p-3 cursor-pointer hover:bg-[#fafafa] flex items-center justify-between transition-colors"
                    onClick={() =>
                      setExpandedEstimate(
                        expandedEstimate === booking?.id ? null : booking?.id || null
                      )
                    }
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {isExpanded ? (
                        <ChevronDown size={16} className="text-[#1a1a2e] flex-shrink-0" />
                      ) : (
                        <ChevronRight size={16} className="text-[#1a1a2e] flex-shrink-0" />
                      )}
                      <FileText size={16} className="text-[#1a1a2e] flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <Text strong className="block truncate text-[#1a1a2e]">
                          {booking?.referenceNumber}
                        </Text>
                        <Text className="text-xs text-[#1a1a2e]">
                          {booking?.createdAt ? formatDate(booking.createdAt) : booking?.bookingDate ? formatDate(booking.bookingDate) : ''}
                        </Text>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <Text strong className="text-[#1a1a2e]">${estimateTotal.toFixed(2)}</Text>
                      </div>
                      {!hasInvoice && (
                        <span className="inline-block px-2 py-1 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg bg-gray-200 text-[#1a1a2e]">
                          Estimated
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Estimate Details */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-[#e5e5e5] bg-white">
                      {/* Services */}
                      {estimatedServices && estimatedServices.length > 0 && (
                        <div className="mt-3 space-y-3">
                          <Text strong className="text-sm block mb-2 text-[#1a1a2e] font-bold uppercase">
                            Services ({estimatedServices.length})
                          </Text>
                          {estimatedServices.map((service: TServiceTicket) => {

                            // Get location display
                            const locationDisplay = service.location
                              ? (typeof service.location === 'string' 
                                  ? service.location 
                                  : `${service.location.address || ""}${service.location.city ? `, ${service.location.city}` : ""}${service.location.state ? `, ${service.location.state}` : ""}${service.location.zip ? ` ${service.location.zip}` : ""}`.trim())
                              : service.tempLocationData
                              ? `${service.tempLocationData.address || ""}${service.tempLocationData.city ? `, ${service.tempLocationData.city}` : ""}${service.tempLocationData.state ? `, ${service.tempLocationData.state}` : ""}${service.tempLocationData.zip ? ` ${service.tempLocationData.zip}` : ""}`.trim()
                              : null;

                            return (
                              <div
                                key={service.id}
                                className="bg-white border border-[#e5e5e5] rounded-lg p-3 space-y-2"
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Text strong className="text-[#1a1a2e] text-sm">
                                        {formatServiceType(service.serviceType)}
                                      </Text>
                                      {getStatusBadge(service.status)}
                                    </div>
                                    {service.serviceDatetime && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <Calendar size={12} className="text-[#1a1a2e]" />
                                        <Text className="text-xs text-[#1a1a2e]">
                                          {formatDate(service.serviceDatetime)}
                                        </Text>
                                      </div>
                                    )}
                                    {service.duration && (
                                      <Text className="text-xs text-[#1a1a2e] block mt-1">
                                        Duration: {service.duration} hours
                                      </Text>
                                    )}
                                    {service.staffs && service.staffs.length > 0 && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <Users size={12} className="text-[#1a1a2e]" />
                                        <Text className="text-xs text-[#1a1a2e]">
                                          Staff: {service.staffs.length}
                                        </Text>
                                      </div>
                                    )}
                                    {service.vessel && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <Truck size={12} className="text-[#1a1a2e]" />
                                        <Text className="text-xs text-[#1a1a2e]">
                                          Vessel: {service.vessel.name}
                                        </Text>
                                      </div>
                                    )}
                                  </div>
                                  {(() => {
                                    // Calculate service price using the same logic as admin
                                    const cost = calculateServiceCost({
                                      service,
                                      artHandlerRole: artHandlerRole || undefined,
                                      addOnServices: addOnServices || [],
                                      serviceOverrides: new Map(), // No overrides in client view
                                    });
                                    return (
                                      <div className="text-right flex-shrink-0 ml-2">
                                        <Text strong className="text-[#1a1a2e]">${cost.total.toFixed(2)}</Text>
                                      </div>
                                    );
                                  })()}
                                </div>

                                {/* Location */}
                                {locationDisplay && (
                                  <div className="pt-2 border-t border-[#e5e5e5]">
                                    <div className="flex items-center gap-1 mb-1">
                                      <MapPin size={12} className="text-[#1a1a2e]" />
                                      <Text className="text-xs font-bold uppercase text-[#1a1a2e]">Location</Text>
                                    </div>
                                    <Text className="text-xs text-[#1a1a2e]">{locationDisplay}</Text>
                                  </div>
                                )}

                                {/* Estimated Item Count */}
                                {service.estimatedItemCount && (
                                  <div className="pt-2 border-t border-[#e5e5e5]">
                                    <div className="flex items-center gap-1 mb-1">
                                      <Package size={12} className="text-[#1a1a2e]" />
                                      <Text className="text-xs font-bold uppercase text-[#1a1a2e]">Estimated Items</Text>
                                    </div>
                                    <Text className="text-xs text-[#1a1a2e] font-mono">{service.estimatedItemCount} items</Text>
                                  </div>
                                )}

                                {/* Inventory Templates */}
                                {service.estimateTemplates && service.estimateTemplates.length > 0 && (
                                  <div className="pt-2 border-t border-[#e5e5e5]">
                                    <div className="flex items-center gap-1 mb-2">
                                      <Package size={12} className="text-[#1a1a2e]" />
                                      <Text className="text-xs font-bold uppercase text-[#1a1a2e]">Inventory Templates</Text>
                                    </div>
                                    <div className="space-y-1.5">
                                      {service.estimateTemplates.map((template, idx) => {
                                        // Note: estimateTemplates structure is { templateId, templateName, quantity, packingType }
                                        return (
                                          <div key={template.templateId || idx} className="bg-white border border-[#e5e5e5] p-2">
                                            <div className="flex items-center justify-between mb-1">
                                              <Text className="text-xs font-bold text-[#1a1a2e]">{template.templateName || `Template ${idx + 1}`}</Text>
                                              {template.quantity && template.quantity > 1 && (
                                                <Text className="text-xs text-[#1a1a2e] font-mono">Qty: {template.quantity}</Text>
                                              )}
                                            </div>
                                            {template.packingType && (
                                              <div className="flex flex-wrap gap-2 text-xs text-[#1a1a2e]">
                                                <span className="text-xs">
                                                  {capitalize(template.packingType.replace(/_/g, " "))}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Notes */}
                                {service.scope && (
                                  <div className="pt-2 border-t border-[#e5e5e5]">
                                    <Text className="text-xs font-bold uppercase text-[#1a1a2e] block mb-1">Notes</Text>
                                    <Text className="text-xs text-[#1a1a2e]">{service.scope}</Text>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Pricing Total */}
                      <div className="mt-3 pt-3 border-t border-[#e5e5e5] flex justify-between items-center">
                        <Text strong className="text-[#1a1a2e]">
                          {!hasInvoice ? "Estimated Total" : "Total Price"}
                        </Text>
                        <Text strong className="text-lg text-[#1a1a2e] font-mono">
                          ${estimateTotal.toFixed(2)}
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {booking.invoiceConfig && (
        <Card size="small" title="Invoice Configuration">
          <Descriptions column={1} size="small">
            {booking.invoiceConfig.requestedDeposit && (
              <Descriptions.Item label="Requested Deposit">
                <Text>${booking.invoiceConfig.requestedDeposit.toFixed(2)}</Text>
              </Descriptions.Item>
            )}
            {booking.invoiceConfig.dueDate && (
              <Descriptions.Item label="Due Date">
                <Text>{formatDate(booking.invoiceConfig.dueDate)}</Text>
              </Descriptions.Item>
            )}
            {booking.invoiceConfig.netTerms !== undefined && booking.invoiceConfig.netTerms !== null ? (
              <Descriptions.Item label="Net Terms">
                <Text>
                  {booking.invoiceConfig.netTerms === -1 
                    ? "Due Before Delivery" 
                    : `${booking.invoiceConfig.netTerms} days`}
                </Text>
              </Descriptions.Item>
            ) : (
              <Descriptions.Item label="Net Terms">
                <Text type="secondary">Not set</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {visibleServiceTickets.some(s => s.status === JobStatus.ESTIMATED && s.estimateTemplates) && (
        <Card size="small" title="Estimate Information">
          <Text type="secondary" className="text-sm">
            This booking contains estimated services. Final pricing will be confirmed upon approval.
          </Text>
        </Card>
      )}
    </div>
    );
  };

  const billingContent = <BillingContent />;

  const tabItems = [
    {
      key: "services",
      label: `Services (${visibleServiceTickets.length})`,
      children: servicesContent,
    },
    {
      key: "inventory",
      label: `Inventory (${uniqueInventories.length})`,
      children: inventoryContent,
    },
    {
      key: "documents",
      label: `Documents (${documentsData?.items.length || 0})`,
      children: documentsContent,
    },
    {
      key: "billing",
      label: "Billing & Estimate",
      children: billingContent,
    },
  ];

  return (
    <div className="h-full flex relative">
      <div className="h-full flex flex-col bg-white border-l border-neutral-200 flex-1 relative">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 bg-white">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Calendar size={20} className="text-neutral-500 flex-shrink-0" />
            <div className="min-w-0">
              <Title level={5} className="!mb-0 truncate">
                {booking.referenceNumber}
              </Title>
              {booking.project?.name && (
                <Text type="secondary" className="text-xs">
                  {booking.project.name}
                </Text>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-neutral-200 rounded transition-colors flex-shrink-0 ml-2"
          >
            <X size={20} />
          </button>
        </div>

        {/* Booking Overview */}
        <div className="p-4 border-b border-neutral-200">
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Status">
              {getStatusBadge(booking.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Booking Date">
              {formatDate(booking.bookingDate)}
            </Descriptions.Item>
            {booking.sidemark && (
              <Descriptions.Item label="Sidemark">
                {booking.sidemark}
              </Descriptions.Item>
            )}
            {relationshipData?.relationship !== "service-client" && booking.client && (
              <Descriptions.Item label="Client">
                {booking.client.companyName ||
                  `${booking.client.firstName || ""} ${booking.client.lastName || ""}`.trim() ||
                  "N/A"}
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>

        {/* Tabs Content */}
        <div className="flex-1 overflow-auto">
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as BookingDetailTab)}
            items={tabItems}
            className="px-4 py-2"
          />
        </div>
      </div>

      {/* Service Detail Sidebar (nested) - appears to the left of booking sidebar */}
      {isServiceSidebarOpen && selectedService && (
        <>
          <div
            className={`absolute right-full top-0 h-full bg-white shadow-lg transition-all duration-300 ease-in-out border-r border-neutral-200 w-[400px] translate-x-0`}
            style={{ zIndex: 60 }}
          >
            <ServiceDetailSidebar
              service={selectedService}
              booking={booking}
              onClose={handleCloseServiceSidebar}
            />
          </div>
          {/* Overlay for nested sidebar on mobile */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-[45] lg:hidden pointer-events-auto"
            onClick={handleCloseServiceSidebar}
          />
        </>
      )}

      {/* Completion Details Sidebar (nested) - appears to the left of booking sidebar */}
      {isCompletionSidebarOpen && completionService && (
        <>
          <div
            className={`absolute right-full top-0 h-full bg-white shadow-lg transition-all duration-300 ease-in-out border-r border-neutral-200 w-[400px] translate-x-0`}
            style={{ zIndex: 60 }}
          >
            <ServiceCompletionSidebar
              service={completionService}
              booking={booking}
              onClose={handleCloseCompletionSidebar}
            />
          </div>
          {/* Overlay for nested sidebar on mobile */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-[45] lg:hidden pointer-events-auto"
            onClick={handleCloseCompletionSidebar}
          />
        </>
      )}

      {/* Add Service Sidebar (nested) - appears to the left of booking sidebar */}
      {isAddServiceSidebarOpen && booking && (
        <>
          <div
            className={`absolute right-full top-0 h-full transition-all duration-300 ease-in-out w-[500px] translate-x-0`}
            style={{ zIndex: 60 }}
          >
            <AddServiceSidebar
              open={isAddServiceSidebarOpen}
              onClose={() => setIsAddServiceSidebarOpen(false)}
              bookingId={booking.id}
              booking={booking}
            />
          </div>
          {/* Overlay for nested sidebar on mobile */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-[45] lg:hidden pointer-events-auto"
            onClick={() => setIsAddServiceSidebarOpen(false)}
          />
        </>
      )}
    </div>
  );
};

